import type { ComponentType } from 'react'
import { useCallback, useEffect, useMemo } from 'react'

import type { TeamMember } from '@api'
import times from 'lodash/times'
import { useInView } from 'react-intersection-observer'
import { type ListChildComponentProps, VariableSizeList } from 'react-window'

import CloseIcon from '@/assets/icons/icon-close.svg'
import { IconLoading } from '@/assets/icons/icon-loading'
import type {
  Notification,
  NotificationTypeRequest,
  UnreadNotificationsCountResponse,
} from '@/modules/chatting/types'

import { notificationTypes } from '..'
import ReloadIcon from '../../../../icons/reload.svg'
import { IconButton } from '../../../components/icon-button'
import { TagList } from '../../../components/tags-list'
import { useSizeObserver } from '../../../sidebar/use-size-observer'
import { NotificationItem } from './notification-item'
import { NotificationSkeleton } from './notification-item/skeleton'

export interface NotificationDialogProps {
  onClickItem: (type: NotificationTypeRequest) => void
  type: NotificationTypeRequest
  notifications: Notification[]
  onLoadMore: () => void
  canLoadMore: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
  onReload: () => void
  onCloseNotificationsBar: () => void
  unreadNotificationsCountsMap?: UnreadNotificationsCountResponse
  members: TeamMember[]
  isMembersFetching: boolean
}

const ITEM_HEIGHT = 168

export const NotificationsDialogContent = ({
  onClickItem,
  type,
  notifications,
  onLoadMore,
  canLoadMore,
  isFetchingNextPage,
  isLoading,
  onReload,
  onCloseNotificationsBar,
  unreadNotificationsCountsMap,
  members,
  isMembersFetching,
}: NotificationDialogProps) => {
  const [parentRef, width, height] = useSizeObserver()
  const { ref, inView } = useInView()

  useEffect(() => {
    if (inView && canLoadMore) {
      onLoadMore()
    }
  }, [inView, canLoadMore, onLoadMore])

  const renderNotificationItem: ComponentType<
    ListChildComponentProps<Notification[]>
  > = useCallback(
    ({ data, index, style }) => {
      const notification = data[index]
      const { user } = notification

      if (!user) {
        return null
      }

      return (
        <div
          style={style}
          ref={index === data.length - 1 ? ref : undefined}
        >
          <NotificationItem
            members={members}
            isMembersFetching={isMembersFetching}
            notification={data[index]}
            onCloseNotificationsBar={onCloseNotificationsBar}
            fan={user}
          />
        </div>
      )
    },
    [onCloseNotificationsBar, ref, isMembersFetching, members],
  )

  const notificationTypesWithBadges = useMemo<
    ((typeof notificationTypes)[number] & { badge?: number })[]
  >(() => {
    if (!unreadNotificationsCountsMap) {
      return notificationTypes
    }

    const isTypeExist = (
      type: NotificationTypeRequest,
    ): type is keyof UnreadNotificationsCountResponse =>
      type in unreadNotificationsCountsMap

    return notificationTypes.map((type) => {
      if (isTypeExist(type.id)) {
        const count = unreadNotificationsCountsMap[type.id]

        return {
          ...type,
          badge: count,
        }
      }

      return type
    })
  }, [unreadNotificationsCountsMap])

  const getItemSize = useCallback(() => ITEM_HEIGHT, [])

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b px-6 py-3">
        <p className="text-xl font-semibold leading-[30px]">Notifications</p>
        <div className="flex items-center gap-3">
          <IconButton
            icon={ReloadIcon}
            onClick={onReload}
          />
          <IconButton
            icon={CloseIcon}
            iconClassName="h-6 w-6"
            onClick={onCloseNotificationsBar}
          />
        </div>
      </div>
      <div className="border-b p-2">
        <TagList
          list={notificationTypesWithBadges}
          onClickItem={onClickItem}
          selectedItems={[type]}
          listClassName="gap-5 py-2"
          tagClassName="bg-secondary-50 dark:text-secondary-200 dark:bg-secondary-600 aria-selected:bg-primary-500 dark:aria-selected:bg-primary-500 aria-selected:text-white dark:aria-selected:text-white hover:bg-primary-50 dark:hover:bg-secondary-500 px-4"
        />
      </div>
      <div className="flex-1 overflow-hidden px-4">
        <div
          className="relative h-full max-h-full w-full flex-1 space-y-6 overflow-hidden py-4"
          ref={parentRef}
        >
          {isLoading ? (
            times(5).map((num) => <NotificationSkeleton key={num} />)
          ) : (
            <VariableSizeList
              className="scrollbar-hide"
              itemData={notifications}
              itemSize={getItemSize}
              height={height}
              itemCount={notifications.length}
              width={width}
            >
              {renderNotificationItem}
            </VariableSizeList>
          )}

          {isFetchingNextPage && (
            <div className="bg-card absolute bottom-0 left-0 right-0">
              <IconLoading className="mx-auto my-3 animate-spin" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
